package com.xtream.ui.ui.layout

import androidx.compose.animation.core.animateFloatAsState
import androidx.compose.animation.core.tween
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.BoxScope
import androidx.compose.foundation.layout.PaddingValues
import androidx.compose.foundation.layout.fillMaxHeight
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.lazy.LazyColumn
import androidx.compose.foundation.lazy.LazyListScope
import androidx.compose.foundation.lazy.itemsIndexed
import androidx.compose.foundation.lazy.rememberLazyListState
import androidx.compose.runtime.Composable
import androidx.compose.runtime.SideEffect
import androidx.compose.runtime.derivedStateOf
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableIntStateOf
import androidx.compose.runtime.mutableStateMapOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.saveable.rememberSaveable
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.focus.FocusRequester
import androidx.compose.ui.focus.focusRequester
import androidx.compose.ui.focus.focusRestorer
import androidx.compose.ui.focus.onFocusChanged
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.graphics.graphicsLayer
import androidx.compose.ui.unit.Dp
import androidx.compose.ui.unit.dp
import androidx.lifecycle.compose.LifecycleResumeEffect

enum class TvColumnHeaderMode { Hidden, Pinned, AutoHide }
data class TvColumnConfig(
    val headerMode: TvColumnHeaderMode = TvColumnHeaderMode.AutoHide,
    val animationDuration: Int = 300,
    val verticalSpacing: Dp = 16.dp,
    val headerPadding: PaddingValues = PaddingValues(48.dp),
    val contentPadding: PaddingValues=PaddingValues( bottom = 48.dp),
    val headerHorizontalSpacing: Dp = 16.dp,
    val backgroundScrimColor: Color = Color.Unspecified,
    val backgroundAlignment: Alignment = Alignment.TopEnd,
    val backgroundFillFraction: Float = 1f,
    val backgroundHidesWithHeader: Boolean = false,

    )

@Composable
fun <T> TvContentScreen(
    items: List<T>,
    columnFocusRequester: FocusRequester,
    defaultRowFocusRequester: FocusRequester,
    modifier: Modifier = Modifier,
    config: TvColumnConfig = TvColumnConfig(),
    itemKey: ((index: Int, item: T) -> Any) = { index, _ -> "row_$index" },
    backgroundContent: (@Composable BoxScope.() -> Unit)? = null,
    header: (@Composable () -> Unit)? = null,
    // ✅ Extra items appended after the main list (ads, banners, etc.)
    footer: (LazyListScope.() -> Unit)? = null,
    itemRow: @Composable (index: Int, item: T, rowFocusRequester: FocusRequester) -> Unit,
) {
    val listState = rememberLazyListState()
    val lastFocusedRow = rememberSaveable { mutableIntStateOf(0) }
    val rowFocusRequesters = remember { mutableStateMapOf<Int, FocusRequester>() }

    val isHeaderVisible by remember {
        derivedStateOf {
            listState.firstVisibleItemIndex == 0 &&
                    listState.firstVisibleItemScrollOffset == 0
        }
    }

    val alpha by animateFloatAsState(
        targetValue = when (config.headerMode) {
            TvColumnHeaderMode.Hidden -> 0f
            TvColumnHeaderMode.Pinned -> 1f
            TvColumnHeaderMode.AutoHide -> if (isHeaderVisible) 1f else 0f
        },
        animationSpec = tween(config.animationDuration), label = "headerAlpha"
    )

    val translationY by animateFloatAsState(
        targetValue = when (config.headerMode) {
            TvColumnHeaderMode.Hidden -> -1f
            TvColumnHeaderMode.Pinned -> 0f
            TvColumnHeaderMode.AutoHide -> if (isHeaderVisible) 0f else -1f
        },
        animationSpec = tween(config.animationDuration), label = "headerTranslationY"
    )

    Box(modifier = modifier.fillMaxSize()) {

        if (backgroundContent != null) {
            Box(
                modifier = Modifier
                    .fillMaxSize(config.backgroundFillFraction)
                    .align(config.backgroundAlignment)
                    .graphicsLayer {
                        this.alpha = if (config.backgroundHidesWithHeader) alpha else 1f
                    }
            ) { backgroundContent() }
        }
            LazyColumn(
                state = listState,
                modifier = Modifier
                    .fillMaxHeight()
                    .focusRequester(columnFocusRequester)

                    .focusRestorer (
                       rowFocusRequesters[lastFocusedRow.intValue] ?: FocusRequester.Default
                    ),
                verticalArrangement = Arrangement.spacedBy(config.verticalSpacing),
                contentPadding = config.contentPadding
            ) {
                if (header != null) {
                    stickyHeader {
                        Box(
                            modifier = Modifier
                                .padding(config.headerPadding)
                                .graphicsLayer {
                                this.alpha = alpha
                                this.translationY = translationY * size.height
                            }
                        ) { header() }
                    }
                }

                itemsIndexed(
                    items = items,
                    key = itemKey
                ) { index, item ->
                    val rowFocusRequester = if (index == 0) defaultRowFocusRequester
                    else remember { FocusRequester() }
                    SideEffect { rowFocusRequesters[index] = rowFocusRequester }
                    Box(
                        modifier = Modifier.onFocusChanged {
                            if (it.hasFocus) lastFocusedRow.intValue = index
                        }
                    ) {
                        itemRow(index, item, rowFocusRequester)
                    }
                }

                // ✅ Footer items (ads, banners, etc.)
                footer?.invoke(this)
            }




    }

    LifecycleResumeEffect(Unit) {
       // val restored =
           // columnFocusRequester.restoreFocusedChild()
        //if (!restored) columnFocusRequester.requestFocus()
        onPauseOrDispose {
            columnFocusRequester.saveFocusedChild()
        }
    }
}




@Composable
fun  TvContentScreen(
    columnFocusRequester: FocusRequester,
    defaultRowFocusRequester: FocusRequester,
    modifier: Modifier = Modifier,
    config: TvColumnConfig = TvColumnConfig(),
    backgroundContent: (@Composable BoxScope.() -> Unit)? = null,

    content: LazyListScope.( FocusRequester) -> Unit
) {
    val listState = rememberLazyListState()
    val lastFocusedRow = rememberSaveable { mutableIntStateOf(0) }
    val rowFocusRequesters = remember { mutableStateMapOf<Int, FocusRequester>() }

    val isHeaderVisible by remember {
        derivedStateOf {
            listState.firstVisibleItemIndex == 0 &&
                    listState.firstVisibleItemScrollOffset == 0
        }
    }

    val alpha by animateFloatAsState(
        targetValue = when (config.headerMode) {
            TvColumnHeaderMode.Hidden -> 0f
            TvColumnHeaderMode.Pinned -> 1f
            TvColumnHeaderMode.AutoHide -> if (isHeaderVisible) 1f else 0f
        },
        animationSpec = tween(config.animationDuration), label = "headerAlpha"
    )

    val translationY by animateFloatAsState(
        targetValue = when (config.headerMode) {
            TvColumnHeaderMode.Hidden -> -1f
            TvColumnHeaderMode.Pinned -> 0f
            TvColumnHeaderMode.AutoHide -> if (isHeaderVisible) 0f else -1f
        },
        animationSpec = tween(config.animationDuration), label = "headerTranslationY"
    )

    Box(modifier = modifier.fillMaxSize()) {

        if (backgroundContent != null) {
            Box(
                modifier = Modifier
                    .fillMaxSize(config.backgroundFillFraction)
                    .align(config.backgroundAlignment)
                    .graphicsLayer {
                        this.alpha = if (config.backgroundHidesWithHeader) alpha else 1f
                    }
            ) { backgroundContent() }
        }

            LazyColumn(
                state = listState,
                modifier = Modifier
                    .fillMaxHeight()
                    .focusRequester(columnFocusRequester)
                    .focusRestorer (
                        rowFocusRequesters[lastFocusedRow.intValue] ?: FocusRequester.Default
                    ),
                verticalArrangement = Arrangement.spacedBy(config.verticalSpacing),
                contentPadding = PaddingValues(top = 4.dp, bottom = 48.dp),
            ) {
                content(this, rowFocusRequesters[lastFocusedRow.intValue] ?: FocusRequester.Default)
            }





    }

    LifecycleResumeEffect(Unit) {
        // val restored =
        columnFocusRequester.restoreFocusedChild()
        //if (!restored) columnFocusRequester.requestFocus()
        onPauseOrDispose {
            columnFocusRequester.saveFocusedChild()
        }
    }
}